// Una lectura multiplanta encuestada (PP-64). Mismo criterio que la bandeja
// (requests/useInbox.ts): polling, sin websockets (PLAN-ASIGNACION-PROVEEDOR
// §10); vuelve a preguntar al recuperar el foco; una respuesta vieja se
// descarta; el error no entra al estado, solo su tipo.
//
// Cada bloque del Inicio tiene la suya: si una lectura falla, los demas
// bloques siguen en pantalla. La de ofertas es del portal, compartida con el
// aviso de oferta nueva (offer-alert/OffersFeed.tsx, PP-71); el Mapa usa la
// suya.
import { useCallback, useEffect, useRef, useState } from "react";
import type { Clock } from "../requests/clock.js";
import { MultiSiteError } from "./port.js";

export type ReadState<T> =
  | { estado: "cargando" }
  | { estado: "denegado" }
  | { estado: "error" }
  | {
      estado: "listo";
      data: T;
      /** La ultima actualizacion fallo: se muestra lo anterior y se reintenta en la proxima vuelta. */
      stale: boolean;
    };

export interface PolledHandle<T> {
  state: ReadState<T>;
  refresh: () => void;
}

function hidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * `read` tiene que ser estable (useCallback): cambiarla es otra lectura y
 * vuelve a "cargando". Con `whenHidden` la encuesta sigue con la pestana
 * oculta: es la de las ofertas, que tiene que avisar aunque el operador este
 * en otra pestana. Con `enabled` en `false` no pregunta nada y queda en
 * "cargando": la encuesta de ofertas vive arriba del shell y no pregunta
 * mientras la cuenta no tiene plantas.
 */
export function usePolled<T>(read: () => Promise<T>, everyMs: number, clock: Clock, whenHidden = false, enabled = true): PolledHandle<T> {
  const [state, setState] = useState<ReadState<T>>({ estado: "cargando" });
  const sequence = useRef(0);

  const refresh = useCallback(() => {
    sequence.current += 1;
    const mine = sequence.current;
    read()
      .then((data) => {
        if (mine === sequence.current) setState({ estado: "listo", data, stale: false });
      })
      .catch((error: unknown) => {
        if (mine !== sequence.current) return;
        if (error instanceof MultiSiteError && error.kind === "access") {
          setState({ estado: "denegado" });
          return;
        }
        setState((previous) => (previous.estado === "listo" ? { ...previous, stale: true } : { estado: "error" }));
      });
  }, [read]);

  useEffect(() => {
    setState({ estado: "cargando" });
    if (!enabled) return;
    refresh();
    return () => {
      sequence.current += 1;
    };
  }, [refresh, enabled]);

  useEffect(() => {
    if (!enabled) return;
    return clock.every(everyMs, () => {
      if (whenHidden || !hidden()) refresh();
    });
  }, [clock, everyMs, whenHidden, refresh, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (!hidden()) refresh();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh, enabled]);

  return { state, refresh };
}
